import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import SrPriceComparisonCard from 'client/components/SrPriceComparisonCard/SrPriceComparisonCard';
import SrListingLowerPriceModal from 'client/components/SrListingLowerPriceModal/SrListingLowerPriceModal';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import useSrComparativeMarketAnalysisCardController from 'client/components/SrComparativeMarketAnalysisCard/useSrComparativeMarketAnalysisCardController';
import SrCard from 'client/components/SrCard/SrCard';
import SrFlexRow from 'client/components/SrFlexRow/SrFlexRow';
import SrFlex from 'client/components/SrFlex';

const SrComparativeMarketAnalysisCard = forwardRef(
  ({ listing, appUrl, userAgent, dispatch, onLowerRentPrice, partner, isAdmin }, ref) => {
    const {
      lowerPriceModal,
      cma,
      listingHasSunSpot,
    } = useSrComparativeMarketAnalysisCardController({ listing });

    useImperativeHandle(
      ref,
      () => {
        return {
          setShouldCollapseCMA(shouldCollapseCMA) {
            cma.setShouldCollapseCMA(shouldCollapseCMA);
          },
        };
      },
      [cma],
    );

    if (!cma.hasCMAUpToDate) {
      return (
        <SrCard padding="16px" maxWidth="100%">
          <SrText
            font="greycliff"
            fontSize="18px"
            weight="700"
            lineHeight="25px"
            color="blueGreycliff"
            marginLeft="16px"
          >
            Comparative Market Analysis
          </SrText>
          <SrFlexRow
            margin="16px 16px 0px 16px"
            padding="16px 8px"
            backgroundColor="#FFFBEB"
            borderRadius="8px"
          >
            <SrText font="greycliff" fontSize="14px" lineHeight="25px" color="blueGreycliff">
              This property does not have a Comparative Market Analysis.
            </SrText>
          </SrFlexRow>
        </SrCard>
      );
    }

    return (
      <SrCard padding="16px 0px" maxWidth="100%" margin="20px 0">
        {listingHasSunSpot && (
          <SrFlex
            margin="0px 16px"
            padding="16px 8px"
            backgroundColor={cma.shouldCollapseCMA ? 'white' : '#FFFBEB'}
            borderRadius="8px"
            onClick={cma.onToggle}
            cursor="pointer"
          >
            <SrBox width="calc(100% - 150px)">
              <SrFlexRow marginLeft="8px">
                <SrText
                  font="greycliff"
                  fontSize="18px"
                  weight="600"
                  lineHeight="25px"
                  color="blueGreycliff"
                >
                  CMAs are most helpful <u>before</u> you have a SunSpot.
                </SrText>
              </SrFlexRow>
              <SrFlexRow>
                <SrText font="greycliff" fontSize="14px" lineHeight="25px" color="blueGreycliff">
                  CMAs are just a snapshot in time, the SunSpot is more up-to-date.
                </SrText>
              </SrFlexRow>
            </SrBox>
            <SrFlex justify="center" align="center">
              <SrBox>
                <SrText
                  font="greycliff"
                  fontSize="14px"
                  weight="800"
                  lineHeight="25px"
                  color="blue300"
                >
                  {cma.toggleText}
                </SrText>
              </SrBox>
              <SrBox
                marginLeft="8px"
                transform={cma.toggleIconOrientationTransform}
                backgroundImage={`url(${cma.toggleIconUrl})`}
                backgroundSize="contain"
                backgroundRepeat="no-repeat"
                backgroundPosition="center"
                width="10px"
                height="10px"
              />
            </SrFlex>
          </SrFlex>
        )}
        {!cma.shouldCollapseCMA && (
          <>
            <SrBox marginTop="16px" marginLeft="16px">
              <SrText
                font="greycliff"
                fontSize="18px"
                weight="700"
                lineHeight="25px"
                color="blueGreycliff"
              >
                Comparative Market Analysis
              </SrText>
            </SrBox>
            <SrPriceComparisonCard
              listing={listing}
              partner={partner}
              isAdmin={isAdmin}
              userAgent={userAgent}
              appUrl={appUrl}
              openLowerPriceModal={lowerPriceModal.onOpen}
            />
            {lowerPriceModal.isOpen && listing && (
              <SrListingLowerPriceModal
                listing={listing}
                dispatch={dispatch}
                onClose={lowerPriceModal.onClose}
                onLowerRentPrice={onLowerRentPrice}
              />
            )}
          </>
        )}
      </SrCard>
    );
  },
);

SrComparativeMarketAnalysisCard.propTypes = {
  listing: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  appUrl: PropTypes.string.isRequired,
  userAgent: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onLowerRentPrice: PropTypes.func.isRequired,
};

SrComparativeMarketAnalysisCard.displayName = 'SrComparativeMarketAnalysisCard';

export default SrComparativeMarketAnalysisCard;
